/**
 * PlaybookHelpGuide — user-friendly guide explaining playbook concepts
 *
 * Rendered inside GlobalModal when the user clicks the "?" icon in
 * the playbook detail header. Written for end-users (lawyers, compliance).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiBookOpenTextLight,
  PiListBulletsLight,
  PiGlobeLight,
  PiShieldCheckLight,
  PiScalesLight,
  PiTargetLight,
  PiWarningCircleLight,
  PiCaretDownLight,
  PiCaretUpLight,
  PiLightbulbLight,
  PiArrowRightLight,
  PiFileTextLight,
  PiTagLight,
} from 'react-icons/pi';

// ─── Reusable section accordion ──────────────────────────────────────────────

interface SectionProps {
  icon: React.ReactElement;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const Section: React.FC<SectionProps> = ({ icon, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="flex-shrink-0 text-primary-500">{icon}</span>
          <span className="text-sm text-gray-900 dark:text-gray-100">{title}</span>
        </div>
        {open ? (
          <PiCaretUpLight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <PiCaretDownLight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 text-sm text-gray-600 dark:text-gray-300 leading-relaxed space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

const PlaybookHelpGuide: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="px-6 pb-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar-main">
      {/* Intro */}
      <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
        <div className="flex gap-3">
          <PiBookOpenTextLight className="w-6 h-6 text-primary-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-primary-700 dark:text-primary-300">
              {t('What is a Playbook?')}
            </p>
            <p className="text-sm text-primary-600 dark:text-primary-400 mt-1 leading-relaxed">
              {t('A playbook is your firm\'s set of rules and standards for reviewing legal documents. It tells the AI exactly what to look for, what your preferred positions are, and what constitutes a deal-breaker. Think of it as your firm\'s "negotiation cheat sheet" turned into automated review criteria.')}
            </p>
          </div>
        </div>
      </div>

      {/* How it works */}
      <Section
        icon={<PiArrowRightLight className="w-5 h-5" />}
        title={t('How Playbooks Work')}
        defaultOpen={true}
      >
        <p>{t('A playbook is made up of two types of rules that the AI applies when reviewing your documents:')}</p>
        <div className="mt-3 space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-md bg-info-100 dark:bg-info-900/30 flex items-center justify-center">
              <PiListBulletsLight className="w-4 h-4 text-info-600" />
            </div>
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Clause Rules')}</span>
              <p className="text-gray-500 dark:text-gray-400 mt-0.5">{t('Specific rules for individual clause types (e.g., indemnification, termination, confidentiality). Each rule defines what to check for in that type of clause.')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-md bg-success-100 dark:bg-success-900/30 flex items-center justify-center">
              <PiGlobeLight className="w-4 h-4 text-success-600" />
            </div>
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Global Rules')}</span>
              <p className="text-gray-500 dark:text-gray-400 mt-0.5">{t('Cross-cutting rules that apply to the entire agreement, regardless of clause type. These set the overall review posture and escalation thresholds.')}</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Clause Rule anatomy */}
      <Section
        icon={<PiListBulletsLight className="w-5 h-5" />}
        title={t('Understanding Clause Rules')}
      >
        <p>{t('Each clause rule defines how the AI should evaluate a specific type of clause. Here\'s what each field means:')}</p>

        <div className="mt-3 space-y-3">
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-info-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Clause Type')}</span>
              <span className="text-gray-500 dark:text-gray-400"> — {t('The category of clause this rule applies to (e.g., "Confidentiality", "Termination", "Payment Terms").')}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-info-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Display Name')}</span>
              <span className="text-gray-500 dark:text-gray-400"> — {t('A human-friendly name for this specific rule (e.g., "SLA Metrics", "Liability Cap").')}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-info-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Description')}</span>
              <span className="text-gray-500 dark:text-gray-400"> — {t('Explains what this rule checks for. This guides the AI\'s analysis.')}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-success-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Starting Position')}</span>
              <span className="text-gray-500 dark:text-gray-400"> — {t('Your firm\'s ideal or preferred wording for this clause. The AI compares the actual document text against this benchmark.')}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-warning-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Acceptable Variations')}</span>
              <span className="text-gray-500 dark:text-gray-400"> — {t('Alternative wordings or positions your firm can accept during negotiation, even if they differ from the starting position.')}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-danger-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Red Lines')}</span>
              <span className="text-gray-500 dark:text-gray-400"> — {t('Deal-breakers. If the document contains language matching a red line, the AI will flag it as a critical finding that must be addressed.')}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-warning-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Fallback Position')}</span>
              <span className="text-gray-500 dark:text-gray-400"> — {t('The minimum acceptable position if the starting position is rejected during negotiation. This helps the AI suggest pragmatic alternatives.')}</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Risk Weight */}
      <Section
        icon={<PiShieldCheckLight className="w-5 h-5" />}
        title={t('Risk Weight')}
      >
        <p>{t('Each clause rule has a risk weight from 1 to 10 that reflects how important this clause is to your firm:')}</p>
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-success-500" />
            <span>{t('Low (1–3) — Nice to have. Non-compliance is a minor concern.')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-warning-500" />
            <span>{t('Medium (4–6) — Important. Should be reviewed and negotiated where possible.')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-warning-500" />
            <span>{t('High (7–8) — Very important. Non-compliance requires senior attention.')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-danger-500" />
            <span>{t('Critical (9–10) — Essential. Non-compliance is a potential deal-breaker.')}</span>
          </div>
        </div>
        <p className="mt-3 text-gray-500 dark:text-gray-400">
          {t('The risk weight affects the overall risk score calculated during document reviews and determines how findings are prioritized.')}
        </p>
      </Section>

      {/* Global Rules */}
      <Section
        icon={<PiGlobeLight className="w-5 h-5" />}
        title={t('Understanding Global Rules')}
      >
        <p>{t('Global rules set the overall review posture and apply across all clause types. They typically define:')}</p>
        <ul className="mt-2 space-y-1.5 list-disc list-inside">
          <li>{t('Escalation thresholds — when findings must be elevated to senior reviewers')}</li>
          <li>{t('Approval requirements — who can accept deviations from standard positions')}</li>
          <li>{t('General review principles — overarching guidance the AI follows')}</li>
        </ul>
        <p className="mt-3">{t('Each global rule has a severity level (Critical, Major, Minor, Info) that determines how the AI treats violations of that rule.')}</p>
      </Section>

      {/* Agreement Types & Jurisdictions */}
      <Section
        icon={<PiTagLight className="w-5 h-5" />}
        title={t('Agreement Types & Jurisdictions')}
      >
        <p>{t('These metadata fields help organize your playbooks and guide the AI:')}</p>
        <div className="mt-3 space-y-3">
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-info-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Agreement Types')}</span>
              <span className="text-gray-500 dark:text-gray-400"> — {t('The kinds of agreements this playbook is designed for (e.g., "IT Service Agreements", "NDAs", "Employment Contracts"). This helps match the right playbook when starting a review.')}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-info-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Jurisdictions')}</span>
              <span className="text-gray-500 dark:text-gray-400"> — {t('The legal jurisdictions the playbook\'s rules are based on. Different jurisdictions may have different legal requirements and standards.')}</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Importing from Document */}
      <Section
        icon={<PiFileTextLight className="w-5 h-5" />}
        title={t('Importing from a Document')}
      >
        <p>{t('You can create a playbook manually or import rules automatically from an existing document:')}</p>
        <div className="mt-3 space-y-3">
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-info-500" />
            <span>{t('Upload your firm\'s existing playbook, negotiation guidelines, or standards document (PDF, DOCX, DOC, or TXT).')}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-info-500" />
            <span>{t('The AI reads the document and automatically extracts clause rules, global rules, agreement types, and jurisdictions.')}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-info-500" />
            <span>{t('Review the extracted rules and fine-tune them as needed. You can edit, add, or remove any rule after extraction.')}</span>
          </div>
        </div>
      </Section>

      {/* Tips */}
      <div className="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg p-4 mt-2">
        <p className="text-sm text-info-700 dark:text-info-300 mb-2"><PiLightbulbLight className='w-4 h-4 text-gray-900 dark:text-gray-200 inline-block mr-1' /> {t('Tips')}</p>
        <ul className="text-sm text-info-600 dark:text-info-400 space-y-1.5 list-disc list-inside">
          <li>{t('Start by importing from your firm\'s existing playbook document — it saves a lot of manual work.')}</li>
          <li>{t('Assign higher risk weights to clauses that matter most to your practice area.')}</li>
          <li>{t('Keep red lines concise and specific — they\'re used to detect deal-breakers.')}</li>
          <li>{t('Use global rules for firm-wide policies that apply regardless of clause type.')}</li>
          <li>{t('Playbooks can be shared with your team and reused across multiple reviews.')}</li>
        </ul>
      </div>
    </div>
  );
};

export default PlaybookHelpGuide;
